/**
 * useWidgetState
 *
 * The contract every multi-step widget opts into (videos watched,
 * checklist progress, multi-page form, current step, …) so that
 * mid-widget state survives:
 *   • tab close / refresh
 *   • route change away and back
 *   • cross-device login (fed by the server `widget_states` JSONB)
 *
 * Usage:
 *   const [{ watched, currentIndex }, patch] = useWidgetState(
 *     'video_carousel',                   // key — see below
 *     { watched: [] as string[], currentIndex: 0 }
 *   );
 *
 *   function markWatched(id: string) {
 *     patch({ watched: [...watched, id] });
 *   }
 *
 * Keying:
 *   The widget passes a SHORT name (e.g. 'video_carousel'). The hook
 *   composes the full server key as "<currentChatId>::<widgetName>"
 *   so two instances of the same widget in different chats don't
 *   collide. The chatId is read from the manager context.
 *
 * Behaviour outside a FlowLifecycleManager:
 *   The hook still works — falls back to a pure `useState` so legacy
 *   call sites (and tests that mount widgets in isolation) don't
 *   crash. Persistence is best-effort.
 *
 * Network model:
 *   - Optimistic local update on every `patch()` call.
 *   - PATCH /widget-state debounced 500ms (collapses bursts of clicks).
 *   - On error, the local state stays — we don't roll back, because
 *     the widget continues rendering and the next patch will retry.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { widgetRegistry } from '@tonnie/copilot';
import { useFlowLifecycleContext } from './flow-lifecycle-context';

const DEBOUNCE_MS = 500;
/**
 * Retry policy for widget patches. Three attempts with capped
 * exponential backoff covers the common transient failure modes
 * (server cold-start, network hiccup, brief 5xx) without holding
 * a buggy widget hostage for a worst-case 8+ seconds. After
 * exhaustion we surface `hasSaveError` via the manager so the host's
 * `<AutosaveIndicator>` flips to "Couldn't save" — the user is
 * informed; the next user-driven patch starts a fresh attempt.
 */
const RETRY_DELAYS_MS = [400, 1200] as const;

/**
 * Get the current widget's schema version (if declared) so we can
 * stamp it on persisted state. Future reads use this to detect
 * version skew via `widgetRegistry.sanitizeState` and gracefully fall
 * back to defaults instead of crashing on a renamed field.
 *
 * Untyped registry surface — `WidgetDefinition['stateVersion']` is
 * widget-specific and not exposed in the public types barrel.
 */
function getWidgetStateVersion(widgetName: string): number | undefined {
  const def = widgetRegistry.get(widgetName) as { stateVersion?: number } | undefined;
  return def?.stateVersion;
}

/**
 * Sleep without holding a long-leading delay. The hook's debounce +
 * retry policy means the longest contiguous wait is ~1.2s, well
 * under any timer-cap a reviewer should be concerned about.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Validate persisted widget state through the registry. If the schema
 * rejects (renamed field, schema-version skew, garbage), drop it and
 * fall through to defaults — the widget renders cleanly instead of
 * crashing on a malformed prop access.
 *
 * Logs to the console in dev so authors notice their bump-the-version
 * moment immediately. Production telemetry can hook in here later.
 */
function sanitizePersistedWidgetState(
  widgetName: string,
  persisted: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!persisted) return undefined;
  const result = widgetRegistry.sanitizeState(widgetName, persisted);
  if (result.dropped) {
    if (typeof console !== 'undefined') {
      console.warn(
        `[useWidgetState] Dropping persisted state for "${widgetName}" — ${result.reason ?? 'unknown reason'}`
      );
    }
    return undefined;
  }
  // `unknown_widget_kept` is the registry's "permissive keep" path
  // (companion sub-widgets / internal modals). Quiet by design — not
  // a problem worth flooding the console on every render.
  return result.state;
}

export interface UseWidgetStateOptions {
  /**
   * Override the chatId used in the server key. Useful for tests or
   * for widgets that want to share state across multiple chats (rare).
   * Defaults to the manager's current chat (TODO: wire when the
   * manager exposes it; for now widgets pass an explicit key prefix
   * if needed).
   */
  chatIdOverride?: string;
}

export interface UseWidgetStateMeta {
  isSyncing: boolean;
  isInManager: boolean;
  /**
   * Drain any pending debounced patch immediately. Resolves once the
   * PATCH lands (or no-ops if nothing is pending). The
   * `<FlowLifecycleManager>` calls this on every widget right before
   * an atomic `finalize` so the server's widget snapshot is in sync
   * the moment status flips to 'completed'.
   */
  flush: () => Promise<void>;
}

/**
 * `patch` accepts either a partial object OR an updater function that
 * receives the LATEST local state and returns a partial. The updater form
 * is required when consumers compute the next value from the previous one
 * (e.g. `setChildren((prev) => prev.map(...))`) — passing a partial built
 * from a captured `persisted` value reads stale closure state, so a fast
 * burst of typing can wipe entries the user just added. Pre-2026-04-28
 * the only signature was `(partial)` and the household widget hit exactly
 * this trap: every keystroke wrote `children: []` because its wrapper
 * setters captured the first-render `persisted.children`.
 */
export type WidgetStatePatch<T extends Record<string, unknown>> =
  Partial<T> | ((prev: T) => Partial<T>);

export function useWidgetState<T extends Record<string, unknown>>(
  widgetName: string,
  defaultState: T,
  options: UseWidgetStateOptions = {}
): [T, (partial: WidgetStatePatch<T>) => void, UseWidgetStateMeta] {
  const ctx = useFlowLifecycleContext();
  const isInManager = ctx !== null;

  // Server key. Without a chatId we just use widgetName directly —
  // collisions are the host's problem in that case.
  const widgetKey =
    options.chatIdOverride !== undefined ? `${options.chatIdOverride}::${widgetName}` : widgetName;

  // Hydrate from server snapshot if available, otherwise default.
  // Run through the registry's sanitizer so a renamed/version-skewed
  // widget gracefully falls back to defaults instead of crashing on
  // mount when an old persisted shape is read.
  const sanitized = sanitizePersistedWidgetState(widgetName, ctx?.widgetStates?.[widgetKey]);
  const initial: T = sanitized ? ({ ...defaultState, ...sanitized } as T) : defaultState;

  const [local, setLocal] = useState<T>(initial);
  const [isSyncing, setIsSyncing] = useState(false);

  // Re-hydrate when the server snapshot changes (e.g. after the
  // manager re-fetches following a focus event). We only overwrite
  // local state if the server has a value AND it survives schema
  // validation — never blow away the user's in-flight clicks with an
  // empty/stale server snapshot.
  useEffect(() => {
    const fromServer = sanitizePersistedWidgetState(widgetName, ctx?.widgetStates?.[widgetKey]);
    if (fromServer && Object.keys(fromServer).length > 0) {
      setLocal((prev) => ({ ...prev, ...fromServer }) as T);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.widgetStates?.[widgetKey], widgetName]);

  // Debounced flush to the server. We track the timer in a ref to
  // coalesce bursts. The timer is cleared on unmount so we don't
  // accidentally fire after the widget is gone (the patch will be
  // re-attempted on the next mount via the optimistic local copy + the
  // server's stored state).
  const pendingPatchRef = useRef<Partial<T>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Send a patch with retry-with-backoff. On exhaustion we let the
   * manager flip `hasSaveError` (it does so internally on
   * `patchWidgetState` rejection); the next successful patch clears
   * the indicator. Local optimistic state survives all failure
   * modes — the data is never lost from the user's screen.
   */
  const sendPatchWithRetry = useCallback(
    async (toSend: Record<string, unknown>): Promise<void> => {
      if (!ctx || Object.keys(toSend).length === 0) return;
      let lastErr: unknown = null;
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        try {
          await ctx.patchWidgetState(widgetKey, toSend);
          return;
        } catch (err) {
          lastErr = err;
          if (attempt < RETRY_DELAYS_MS.length) {
            await delay(RETRY_DELAYS_MS[attempt]);
          }
        }
      }
      // All attempts exhausted — let the error propagate so the
      // manager's `patchWidgetState` catch block runs and toggles
      // `hasSaveError`. We don't throw silently because the manager's
      // autosave indicator depends on the rejection.
      throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
    },
    [ctx, widgetKey]
  );

  const patch = useCallback(
    (partialOrUpdater: WidgetStatePatch<T>) => {
      // Optimistic local merge AND remote coalesce — both must read the
      // SAME resolved partial. The earlier version pulled `resolved`
      // out via a closure-bound `let` and merged it into `pendingPatchRef`
      // AFTER `setLocal` returned, but React queues the setLocal updater
      // for the next render commit — so `resolved` was still `{}` when
      // `pendingPatchRef.current = { ..., ...resolved }` ran. Local
      // state updated correctly (the user saw the change), but the
      // SERVER PATCH shipped an empty diff, and on reload the server
      // returned the original state — silently undoing every "remove"
      // the user clicked. Fixed by merging `pendingPatchRef` INSIDE the
      // updater, where we actually have `resolved`. StrictMode may
      // invoke the updater twice; merging the same partial twice is
      // idempotent (object spread with identical keys is a no-op).
      setLocal((prev) => {
        const resolved =
          typeof partialOrUpdater === 'function'
            ? (partialOrUpdater as (p: T) => Partial<T>)(prev)
            : partialOrUpdater;
        pendingPatchRef.current = { ...pendingPatchRef.current, ...resolved };
        return { ...prev, ...resolved } as T;
      });
      if (!ctx) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsSyncing(true);
      timerRef.current = setTimeout(async () => {
        const toSend = pendingPatchRef.current;
        pendingPatchRef.current = {};
        const stateVersion = getWidgetStateVersion(widgetName);
        const stamped =
          stateVersion !== undefined ? { ...toSend, __version: stateVersion } : toSend;
        try {
          await sendPatchWithRetry(stamped as Record<string, unknown>);
        } catch {
          // Swallow — `ctx.patchWidgetState` already flipped
          // `hasSaveError` via the manager. Throwing here would
          // bubble into an unhandled promise rejection.
        } finally {
          setIsSyncing(false);
        }
      }, DEBOUNCE_MS);
    },
    [ctx, widgetName, sendPatchWithRetry]
  );

  /**
   * Drain any debounced patch immediately. Returns a Promise that
   * resolves when the PATCH lands (or no-ops if there's nothing
   * pending). Used by the manager's `flushPendingWidgetPatches()` and
   * by the host right before `finalize`. Also called from unmount
   * cleanup so a fast leaver doesn't lose their last click.
   */
  const flush = useCallback(async (): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const toSend = pendingPatchRef.current;
    pendingPatchRef.current = {};
    if (!ctx || Object.keys(toSend).length === 0) {
      setIsSyncing(false);
      return;
    }
    const stateVersion = getWidgetStateVersion(widgetName);
    const stamped = stateVersion !== undefined ? { ...toSend, __version: stateVersion } : toSend;
    try {
      // Flush is the pre-finalize drain; reuse the retry policy so a
      // transient hiccup at finalize time doesn't lose the user's
      // last patch on the worst possible boundary.
      await sendPatchWithRetry(stamped as Record<string, unknown>);
    } finally {
      setIsSyncing(false);
    }
  }, [ctx, widgetName, sendPatchWithRetry]);

  // Register flush with the manager so `flushPendingWidgetPatches()`
  // (called before atomic finalize) can drain THIS widget's pending
  // patch as well as every other widget's.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    if (!ctx?.registerFlush) return;
    return ctx.registerFlush(() => flushRef.current());
  }, [ctx]);

  // Flush on unmount — even with the registry above, an unmount with
  // no surrounding finalize (e.g. user clicked Discard) still needs
  // to push the last debounced patch.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const toSend = pendingPatchRef.current;
      pendingPatchRef.current = {};
      if (ctx && Object.keys(toSend).length > 0) {
        void ctx.patchWidgetState(widgetKey, toSend as Record<string, unknown>);
      }
    };
  }, [ctx, widgetKey]);

  return [local, patch, { isSyncing, isInManager, flush }];
}
